import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Users, ArrowRight } from 'lucide-react';
import { Button } from '../components/Button';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Modal } from '../components/Modal';
import { Input } from '../components/Input';
import { CurrencySelect } from '../components/CurrencySelect';
import { createGroup, getUserGroups, joinGroup } from '../services/groups';
import { useAuth } from '../store/AuthContext';
import { useToast } from '../store/ToastContext';
import { RequireAuth } from '../components/RequireAuth';
import type { Group } from '../types';

export const GroupsPage: React.FC = () => {
  return (
    <RequireAuth>
      <GroupsPageContent />
    </RequireAuth>
  );
};

const GroupsPageContent: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);

  const [groupName, setGroupName] = useState('');
  const [currency, setCurrency] = useState(localStorage.getItem('preferred_currency') ?? 'USD');
  const [joinId, setJoinId] = useState('');

  useEffect(() => {
    if (user) {
      void fetchGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function fetchGroups() {
    try {
      if (!user) return;
      const data = await getUserGroups(user.uid);
      setGroups(data);
    } catch (error) {
      console.error("Error fetching groups:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user) return;

    if (!groupName.trim()) {
      showToast("Please enter a group name", "error");
      return;
    }

    try {
      await createGroup(groupName, currency, user.uid);
      setIsCreateModalOpen(false);
      setGroupName('');
      showToast("Group created successfully!", "success");
      void fetchGroups();
    } catch (error) {
      console.error("Error creating group:", error);
      showToast("Failed to create group", "error");
    }
  };

  const handleJoinGroup = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user) return;

    if (!joinId.trim()) {
      showToast("Please enter a group ID", "error");
      return;
    }

    try {
      await joinGroup(joinId, user.uid);
      setIsJoinModalOpen(false);
      setJoinId('');
      showToast("Joined group successfully!", "success");
      void fetchGroups();
    } catch {
      showToast("Invalid ID or group not found", "error");
    }
  };



  return (
    <div className="h-full flex flex-col">
      {/* Sticky Header */}
      <div className="flex-none flex items-center justify-between px-4 py-3 bg-bg-header border-b border-border-default">
        <div>
          <h1 className="text-2xl font-black text-text-heading">My Groups</h1>
          <p className="text-xs text-text-muted mt-0.5">Manage shared expenses</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => { setIsJoinModalOpen(true); }}>Join</Button>
          <div className="group relative">
            <Button
              size="sm"
              onClick={() => { setIsCreateModalOpen(true); }}
              disabled={user?.isAnonymous}
            >
              <Plus className="mr-1 h-4 w-4" />
              Create
            </Button>
            {user?.isAnonymous && (
              <div className="absolute top-full left-1/2 mt-2 hidden -translate-x-1/2 rounded bg-bg-card-alt px-2 py-1 text-[10px] text-text-heading group-hover:block whitespace-nowrap z-50 border border-border-default shadow-lg">
                Secure account to create groups
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="h-full flex items-center justify-center pt-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : groups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="mb-4 rounded-full bg-bg-card-alt p-4">
              <Users className="h-8 w-8 text-text-faint" />
            </div>
            <h3 className="text-lg font-medium text-text-heading">No groups yet</h3>
            <p className="mt-1 text-sm text-text-muted">
              Create a group or join one via ID to start.
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {groups.map((group) => (
              <div
                key={group.id}
                onClick={() => navigate(`/group/${group.id}`)}
                className="flex items-center justify-between rounded-xl border border-border-default bg-bg-card p-4 shadow-sm transition-all hover:shadow-md cursor-pointer active:scale-[0.98]"
              >
                <div>
                  <h3 className="font-bold text-text-heading">{group.name}</h3>
                </div>
                <Button variant="ghost" size="icon">
                  <ArrowRight className="h-5 w-5 text-text-faint" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => { setIsCreateModalOpen(false); }}
        title="Create New Group"
      >
        <form onSubmit={handleCreateGroup} className="space-y-4">
          <Input
            label="Group Name"
            placeholder="e.g. Home Bills"
            value={groupName}
            onChange={(e) => { setGroupName(e.target.value); }}
          />
          <CurrencySelect
            label="Default Currency"
            value={currency}
            onChange={setCurrency}
          />
          <Button type="submit" className="w-full">Create Group</Button>
        </form>
      </Modal>

      {/* Join Modal */}
      <Modal
        isOpen={isJoinModalOpen}
        onClose={() => { setIsJoinModalOpen(false); }}
        title="Join Group"
      >
        <form onSubmit={handleJoinGroup} className="space-y-4">
          <Input
            label="Group ID"
            placeholder="e.g. ABC..."
            value={joinId}
            onChange={(e) => { setJoinId(e.target.value); }}
          />
          <Button type="submit" className="w-full">Join</Button>
        </form>
      </Modal>
    </div>
  );
};
